import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Form, Modal, Button } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import TableUI from "../../components/TableUI";
import { ActionButton } from "../../components/Buttons";
import { MdOutlineDelete, MdAdd, MdSearch, MdVisibility } from "react-icons/md";
import { LiaEditSolid } from "react-icons/lia";
import PageTitle from "../../components/PageTitle";
import NotifyData from "../../components/NotifyData";
import Dialog from "../../components/Dialog";
import {
  fetchCraftDesigns,
  addCraftDesign,
  updateCraftDesign,
  deleteCraftDesign,
} from "../../slice/CraftDesignSlice";

const CraftDesignList = ({ onViewDetails, onCreateNew }) => {
  const dispatch = useDispatch();
  const { designs = [], status } = useSelector((state) => state.craftDesigns || {});

  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleDeleteClick = (id, name) => {
    setDeleteTarget({ id, name });
  };

  const handleConfirmDelete = async () => {
    if (deleteTarget) {
      try {
        await dispatch(deleteCraftDesign(deleteTarget.id)).unwrap();
        NotifyData("Craft Design deleted successfully!", "success");
      } catch (err) {
        NotifyData("Failed to delete design", "error");
      } finally {
        setDeleteTarget(null);
      }
    }
  };

  const [formData, setFormData] = useState({
    company_name: "",
    tagline: "",
    address_line1: "",
    phone: "",
    email: "",
    whatsapp: "",
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch(fetchCraftDesigns(searchTerm));
    }, 300);
    return () => clearTimeout(timer);
  }, [dispatch, searchTerm]);

  const handleOpenModal = (design = null) => {
    if (design) {
      setEditMode(true);
      setSelectedId(design.id);
      setFormData({
        company_name: design.company_name || "",
        tagline: design.tagline || "",
        address_line1: design.address_line1 || "",
        phone: design.phone || "",
        email: design.email || "",
        whatsapp: design.whatsapp || "",
      });
    } else {
      setEditMode(false);
      setSelectedId(null);
      setFormData({
        company_name: "",
        tagline: "",
        address_line1: "",
        phone: "",
        email: "",
        whatsapp: "",
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => setShowModal(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.company_name.trim()) {
      NotifyData("Company Name is required", "error");
      return;
    }

    try {
      if (editMode) {
        await dispatch(updateCraftDesign({ id: selectedId, ...formData })).unwrap();
        NotifyData("Craft Design updated successfully!", "success");
      } else {
        await dispatch(addCraftDesign(formData)).unwrap();
        NotifyData("Craft Design created successfully!", "success");
      }
      handleCloseModal();
    } catch (err) {
      NotifyData(err?.message || "Operation failed", "error");
    }
  };

  const handleDelete = async (id, name) => {
    if (window.confirm(`Are you sure you want to delete "${name}"?`)) {
      try {
        await dispatch(deleteCraftDesign(id)).unwrap();
        NotifyData("Craft Design deleted successfully!", "success");
      } catch (err) {
        NotifyData("Failed to delete design", "error");
      }
    }
  };

  const columns = [
    {
      name: "S.No",
      selector: (row, index) => index + 1,
      sortable: true,
      width: "80px",
    },
    {
      name: "Company Name",
      selector: (row) => row.company_name,
      sortable: true,
    },
    {
      name: "Phone",
      selector: (row) => row.phone || "-",
      sortable: true,
    },
    {
      name: "Email",
      selector: (row) => row.email || "-",
      sortable: true,
    },
    {
      name: "Created Date",
      selector: (row) => row.created_at || "-",
      sortable: true,
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="d-flex align-items-center gap-2">
          <ActionButton
            type="button"
            className="btn btn-sm btn-info text-white me-1"
            title="View Configurations & Preview"
            onClick={() => onViewDetails(row)}
          >
            <MdVisibility size={18} />
          </ActionButton>
          <ActionButton
            type="button"
            className="btn btn-sm btn-warning text-white me-1"
            title="Edit Design Config"
            onClick={() => handleOpenModal(row)}
          >
            <LiaEditSolid size={18} />
          </ActionButton>
          <ActionButton
            type="button"
            className="btn btn-sm btn-danger me-1"
            title="Delete Design"
            onClick={() => handleDeleteClick(row.id, row.company_name)}
          >
            <MdOutlineDelete size={18} />
          </ActionButton>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "180px",
    },
  ];

  return (
    <div id="main">
      <Container fluid className="py-3">
        <Row className="mb-3 align-items-center">
          <Col sm={6}>
            <PageTitle title="Craft Design Configurations" />
          </Col>
          <Col sm={6} className="text-end">
            <Button
              className="d-inline-flex align-items-center gap-2 px-3 py-2 border-0 rounded-3 text-white fw-semibold"
              style={{
                background: "linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)",
                boxShadow: "0 4px 14px rgba(30, 60, 114, 0.3)",
                transition: "all 0.25s ease-in-out",
                cursor: "pointer"
              }}
              onClick={() => onCreateNew()}
            >
              <MdAdd size={22} /> Create New Craft Design
            </Button>
          </Col>
        </Row>

        <Card className="border-0 shadow-sm mb-4">
          <Card.Body>
            <Row className="mb-3 align-items-center">
              <Col md={4} sm={6}>
                <div className="input-group">
                  <span className="input-group-text bg-white border-end-0">
                    <MdSearch size={20} className="text-muted" />
                  </span>
                  <Form.Control
                    type="text"
                    placeholder="Search by company name, phone or email..."
                    className="border-start-0 ps-0 shadow-none"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </Col>
            </Row>

            <TableUI
              columns={columns}
              data={Array.isArray(designs) ? designs : []}
              pagination
              highlightOnHover
              responsive
              progressPending={status === "loading"}
            />
          </Card.Body>
        </Card>

        {/* Modal for Create/Update Craft Design */}
        <Modal show={showModal} onHide={handleCloseModal} centered>
          <Modal.Header closeButton className="bg-light">
            <Modal.Title className="h5 font-weight-bold">
              {editMode ? "Edit Craft Design Record" : "Create New Craft Design Record"}
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Company Name *</Form.Label>
                <Form.Control
                  type="text"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleChange}
                  placeholder="Enter Company Name"
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Tagline / Subtitle</Form.Label>
                <Form.Control
                  type="text"
                  name="tagline"
                  value={formData.tagline}
                  onChange={handleChange}
                  placeholder="Enter Tagline"
                />
              </Form.Group>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold">Phone Number</Form.Label>
                    <Form.Control
                      type="text"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="Mobile / Phone"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold">WhatsApp</Form.Label>
                    <Form.Control
                      type="text"
                      name="whatsapp"
                      value={formData.whatsapp}
                      onChange={handleChange}
                      placeholder="WhatsApp Number"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Email Address</Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="company@email.com"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Address Line 1</Form.Label>
                <Form.Control
                  type="text"
                  name="address_line1"
                  value={formData.address_line1}
                  onChange={handleChange}
                  placeholder="Full address details..."
                />
              </Form.Group>
            </Modal.Body>
            <Modal.Footer className="bg-light">
              <Button variant="secondary" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                {editMode ? "Update Config" : "Save & Open Designer"}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        {/* Custom Confirmation Dialog for Delete */}
        <Dialog
          isVisible={!!deleteTarget}
          DialogTitle={`Delete "${deleteTarget?.name || "Design"}"?`}
          DialogMessage="Are you sure you want to delete this craft design configuration? This action cannot be undone."
          confirmText="Yes, Delete"
          cancelText="Cancel"
          type="danger"
          onConfirm={handleConfirmDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      </Container>
    </div>
  );
};

export default CraftDesignList;
